import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const RiskAlertCard = ({ client, onGenerateIntervention, onMessageClient }) => {
  const stats = client?.stats;
  const adherenceRate = stats?.adherenceRate || 0;
  const missedStreak = stats?.missedWorkoutStreak || 0;
  const trend = stats?.adherenceTrend;

  const getRiskLevel = () => {
    if (adherenceRate < 40 || missedStreak > 4) return { level: 'Critical', color: 'red', icon: 'AlertOctagon' };
    if (adherenceRate < 60 || missedStreak > 2) return { level: 'High', color: 'orange', icon: 'AlertTriangle' };
    if (adherenceRate < 70 || trend === 'declining') return { level: 'Medium', color: 'yellow', icon: 'AlertCircle' };
    return { level: 'Low', color: 'green', icon: 'CheckCircle' };
  };

  const risk = getRiskLevel();
  const colorClasses = {
    red: 'bg-red-500/10 border-red-500/20 text-red-500',
    orange: 'bg-orange-500/10 border-orange-500/20 text-orange-500',
    yellow: 'bg-yellow-500/10 border-yellow-500/20 text-yellow-500',
    green: 'bg-green-500/10 border-green-500/20 text-green-500'
  };

  const getPrimaryConcern = () => {
    if (missedStreak > 2) return `${missedStreak} consecutive missed workouts`;
    if (trend === 'declining') return 'Declining adherence pattern';
    if (adherenceRate < 50) return 'Low overall adherence';
    if (!stats?.lastActivityDate) return 'No recent activity';
    return 'Engagement monitoring needed';
  };

  return (
    <div className={`border-2 rounded-xl p-5 ${colorClasses?.[risk?.color]}`}>
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-lg ${colorClasses?.[risk?.color]} flex items-center justify-center`}>
            <Icon name={risk?.icon} size={20} className={`text-${risk?.color}-500`} />
          </div>
          <div>
            <h3 className="font-semibold text-foreground">{client?.fullName}</h3>
            <p className={`text-xs font-medium mt-1 text-${risk?.color}-500`}>
              {risk?.level} Risk
            </p>
          </div>
        </div>
      </div>

      <div className="space-y-2 mb-4">
        <div className="flex items-center justify-between text-sm">
          <span className="text-muted-foreground">Adherence Rate:</span>
          <span className="font-semibold text-foreground">{adherenceRate}%</span>
        </div>
        <div className="flex items-center justify-between text-sm">
          <span className="text-muted-foreground">Missed Streak:</span>
          <span className="font-semibold text-foreground">{missedStreak} workouts</span>
        </div>
        <div className="flex items-center justify-between text-sm">
          <span className="text-muted-foreground">Trend:</span>
          <span className="font-semibold text-foreground capitalize">{trend}</span>
        </div>
        {stats?.lastActivityDate && (
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">Last Activity:</span>
            <span className="font-semibold text-foreground">{stats?.lastActivityDate}</span>
          </div>
        )}
      </div>

      <div className="bg-card/50 rounded-lg p-3 mb-4">
        <p className="text-xs text-muted-foreground mb-1">Primary Concern:</p>
        <p className="text-sm font-medium text-foreground">{getPrimaryConcern()}</p>
      </div>

      <div className="flex gap-2">
        <Button
          variant="default"
          size="sm"
          className="flex-1"
          iconName="Lightbulb"
          onClick={onGenerateIntervention}
        >
          Intervene
        </Button>
        <Button
          variant="outline"
          size="sm"
          iconName="MessageCircle"
          onClick={onMessageClient}
        >
          Message
        </Button>
      </div>
    </div>
  );
};

export default RiskAlertCard;